/* eslint-disable prettier/prettier */
"use client";

/**
 * MODAL BEHAVIOR REQUIREMENTS:
 *
 * 1. CLEAR ALL BUTTON:
 *    - Clears all filters and updates URL
 *    - Does NOT close the modal
 *    - User must manually close modal after clearing
 *
 * 2. CLOSING MODAL:
 *    - Mobile: Only via close button (X) in top-right
 *    - Desktop: Only via clicking outside modal area
 *    - Clear All does NOT close modal on either platform
 *
 * 3. APPLY FILTERS BUTTON:
 *    - Applies selected filters and updates URL
 *    - ALWAYS closes modal on both platforms
 *
 * This design allows users to:
 * - Clear filters and continue browsing other filter options
 * - Explicitly choose when to close the modal
 * - Apply filters as the final confirmation step
 */

import { Button } from "@/components/rac/button";
import { DialogContent, DialogOverlay, DialogTrigger } from "@/components/rac/dialog";
import { Sheet, SheetContent, SheetOverlay } from "@/components/rac/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/radix/tooltip";
import { Badge } from "@/components/radix/badge";
import { DesktopFilters, Filters } from "@/components/search/filters/desktop";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useUrlFilters } from "@/hooks/use-url-filters";
import React, { useCallback } from "react";
import { MobileFilters } from "./mobile";

interface PropertyFiltersPopoverProps {
	trigger: React.ReactNode;
	defaultValues?: Partial<Filters>;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	tooltip?: string;
}

export function PropertyFiltersPopover({
	trigger,
	defaultValues,
	isOpen,
	onOpenChange,
	tooltip,
}: PropertyFiltersPopoverProps) {
	const isMobile = useMediaQuery("(max-width: 768px)");
	const { setFilters } = useUrlFilters();

	const open = isOpen;
	const setOpen = onOpenChange;

	// Store ref to form reset functions
	const desktopFormResetRef = React.useRef<(() => void) | null>(null);
	const mobileFormResetRef = React.useRef<(() => void) | null>(null);

	const handleClose = useCallback(
		(_data?: Partial<Filters>) => {
			setOpen(false);
		},
		[setOpen],
	);

	const handleClearFilters = useCallback(() => {
		// Reset the form state first
		if (isMobile && mobileFormResetRef.current) {
			mobileFormResetRef.current();
		} else if (!isMobile && desktopFormResetRef.current) {
			desktopFormResetRef.current();
		}
		// Clear the URL filters using setFilters (empty object)
		// This will update the URL without triggering navigation
		setFilters({});
	}, [setFilters, isMobile]);

	// Define valid user-facing filter keys that should be counted (same as SelectedFilters)
	const validFilterKeys = new Set([
		"locations",
		"propertyType",
		"bedrooms",
		"bathroomsMin",
		"budget",
		"builtupAreaMin",
		"floors",
		"balconiesMin",
		"parkingsMin",
		"rentalValue",
		"facing",
		"furnishing",
		"status",
		"amenities",
		"documents",
		"spaces",
		"waterSources",
		"levelType",
		"maxAge",
		"possession",
		"plotFacing",
	]);

	const activeFiltersCount = defaultValues
		? Object.entries(defaultValues).filter(([key, value]) => {
				// Only show valid user-facing filter keys
				if (!validFilterKeys.has(key)) return false;
				if (key === "bounds") return false;
				if (Array.isArray(value)) return value.length > 0;
				if (typeof value === "object" && value !== null) {
					if ("min" in value && "max" in value) {
						const rangeValue = value as { min: number; max: number };
						return (rangeValue?.min ?? 0) > 0 || (rangeValue?.max ?? 0) > 0;
					}
					return Object.keys(value).length > 0;
				}
				if (typeof value === "string") return !!value;
				if (typeof value === "number") return value > 0;
				return false;
			}).length
		: 0;

	const triggerElement = (
		<div className="relative">
			{trigger}
			{activeFiltersCount > 0 && <Badge className="absolute -top-1 -right-1 z-10">{activeFiltersCount}</Badge>}
		</div>
	);

	const triggerNode = tooltip ? <TooltipTrigger asChild>{triggerElement}</TooltipTrigger> : triggerElement;

	const renderWithTooltip = (content: React.ReactNode) => {
		if (!tooltip) return content;
		return (
			<TooltipProvider>
				<Tooltip delayDuration={300}>
					{content}
					<TooltipContent side="bottom">
						<p>{tooltip}</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		);
	};

	// Mobile: Right-side Sheet
	if (isMobile) {
		return renderWithTooltip(
			<Sheet isOpen={open} onOpenChange={setOpen}>
				{triggerNode}
				<SheetOverlay>
					<SheetContent side="right" className="w-full bg-white border-l" closeButton={false}>
						<div className="flex flex-col h-full">
							{/* Content */}
							<div className="flex-1 overflow-hidden">
								<MobileFilters
									isOpen={open}
									onApplyFilters={handleClose}
									onClose={handleClose}
									onResetForm={resetFn => {
										mobileFormResetRef.current = resetFn;
									}}
								/>
							</div>

							{/* Footer with action buttons */}
							<div className="flex-shrink-0 p-4 border-t bg-white flex gap-3">
								<Button variant="link" className="flex-1" onPress={handleClearFilters}>
									Clear All
								</Button>
								<Button
									variant="default"
									className="flex-1"
									onPress={() => {
										// Trigger form submission in MobileFilters
										const mobileForm = document.querySelector("#mobile-filters-form") as HTMLFormElement;
										if (mobileForm) {
											mobileForm.requestSubmit();
										}
									}}
								>
									Apply Filters
								</Button>
							</div>
						</div>
					</SheetContent>
				</SheetOverlay>
			</Sheet>,
		);
	}

	// Desktop/Tablet: Top Dialog
	return renderWithTooltip(
		<DialogTrigger isOpen={open} onOpenChange={setOpen}>
			{triggerNode}
			<DialogOverlay>
				<DialogContent
					side="top"
					className="w-full max-w-md md:max-w-4xl lg:max-w-6xl h-auto max-h-[90dvh] p-0 overflow-hidden mx-auto relative z-50"
					closeButton={false}
				>
					<div
						className="flex flex-col h-full max-h-[90dvh]"
						onMouseDown={e => {
							const dropdown = window.settlinDropdownRef;
							if (dropdown && dropdown.contains(e.target as Node)) {
								const target = e.target as HTMLElement;
								const isDropdownItem =
									target.closest('[role="option"]') ||
									target.closest(".cursor-pointer") ||
									target.classList.contains("cursor-pointer");

								if (!isDropdownItem) {
									e.stopPropagation();
									e.preventDefault();
								}
							}
						}}
						onClick={e => {
							const dropdown = window.settlinDropdownRef;
							if (dropdown && dropdown.contains(e.target as Node)) {
								const target = e.target as HTMLElement;
								const isDropdownItem =
									target.closest('[role="option"]') ||
									target.closest(".cursor-pointer") ||
									target.classList.contains("cursor-pointer");

								if (!isDropdownItem) {
									e.stopPropagation();
									e.preventDefault();
								}
							}
						}}
					>
						{/* Content */}
						<div className="flex-1 overflow-y-auto">
							<DesktopFilters
								onApplyFilters={handleClose}
								defaultValues={defaultValues || {}}
								className="border-0 rounded-none w-full max-w-md md:max-w-4xl lg:max-w-6xl"
								isMobile={false}
								onClose={handleClose}
								onResetForm={resetFn => {
									desktopFormResetRef.current = resetFn;
								}}
							/>
						</div>

						{/* Footer with action buttons */}
						<div className="flex-shrink-0 p-4 border-t bg-white flex gap-3">
							<Button variant="link" className="flex-1" onPress={handleClearFilters}>
								Clear All
							</Button>
							<Button
								variant="default"
								className="flex-1"
								onPress={() => {
									// This will trigger the form submission in DesktopFilters
									const form = document.querySelector("#property-filters-form") as HTMLFormElement;
									if (form) {
										form.requestSubmit();
									}
								}}
							>
								Apply Filters
							</Button>
						</div>
					</div>
				</DialogContent>
			</DialogOverlay>
		</DialogTrigger>,
	);
}
